import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { failed } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';
import { register, signin, recover, requestReset, resetConfirm } from './identity.mjs';

/**
 * ENROL, EXTRACTED — 2026-08-16.
 *
 * FOUNDER: "actually login and allowing shall be easy". It was not. A learner who reached a gated
 * chapter was told to go and find Progress, which is a dead end wearing an instruction: the refusal
 * did not carry its remedy. Chapter 9 of this ladder makes exactly that point about hooks — a
 * refusal that says only "blocked" reads as the tool being broken.
 *
 * So the form now appears AT the refusal as well as on Progress. It was moved here rather than
 * copied, because gated.jsx already records why: two copies of one moment drift in what they
 * PROMISE, and the one a learner remembers is whichever he read first.
 *
 * `startMode` lets the caller open on sign-in rather than enrol. Nothing else differs — the same
 * component, the same endpoints, the same words.
 */
/**
 * ENROL — the identity a record hangs on.
 *
 * Founder ruling 2026-08-13: one identity, many subscriptions. The id is DERIVED from name and
 * contact, which is what makes it the same person at coc.ankr.in with nothing synchronised between
 * them — and it is also the recovery story: enter the same two things on a new phone and the record
 * is there. That is said plainly below rather than implied, because a learner who thinks he has an
 * account with a password will one day go looking for a reset that does not exist.
 *
 * WHAT IT DOES NOT DO is promise privacy it cannot keep. There is no OTP (founder's standing ruling),
 * so anyone who knows a learner's name and contact can derive his id. The copy says "identifies you",
 * never "secures you".
 */
/* A LINK IN THE ADDRESS BAR IS A MODE. Read once, before the first render, so arriving on a reset
   link opens the right form rather than the sign-in it would otherwise show. Read-only: the token
   is never written to storage, because a reset link left in localStorage outlives the thirty
   minutes it is supposed to be good for. */
const resetTokenInUrl = () => {
  try { return new URLSearchParams(window.location.search).get('reset') || ''; } catch { return ''; }
};

export default function Enrol({ startMode = 'enrol' }) {
  const urlToken = resetTokenInUrl();
  const [mode, setMode] = useState(urlToken ? 'reset' : startMode);   // enrol · signin · recover · reset
  const [f, setF] = useState({ name: '', contact: '', email: '', phone: '', passphrase: '', recoveryCode: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [claimed, setClaimed] = useState(false);
  const [code, setCode] = useState('');            // shown once, then gone forever
  const [sent, setSent] = useState('');            // what the registry said about a reset request
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  /* ASK FOR A LINK. Separate from `go` because it is not a login attempt: it takes two fields, not
     three, and it must succeed identically whether or not the identity exists. */
  const askForLink = async () => {
    setBusy(true); setErr(''); setSent('');
    try {
      const d = await requestReset(API, { name: f.name, contact: f.contact });
      setSent(d.note || 'If that name and contact match an identity here, a reset link is on its way.');
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  const go = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setClaimed(false);
    try {
      const fn = mode === 'enrol' ? register
        : mode === 'signin' ? signin
        : mode === 'reset' ? ((api, v) => resetConfirm(api, { token: urlToken, passphrase: v.passphrase, name: v.name }))
        : recover;
      const d = await fn(API, f);
      /* A recovery code must be READ before the page moves on. Reloading straight past it would be
         the same as never issuing one — and it is the only way back into the record. */
      if (d.recovery_code) { setCode(d.recovery_code); setBusy(false); }
      else window.location.reload();
    } catch (ex) {
      setErr(ex.message); setClaimed(!!ex.alreadyClaimed); setBusy(false);
    }
  };

  if (code) return (
    <div className="g-glass shell-panel">
      <div className="g-label">Written once, never shown again</div>
      <h3 className="g-h2 enr-h">Your recovery code</h3>
      <p className="g-body">
        Write this on paper. It is the only way back into your record if you forget your passphrase —
        we cannot recover it for you, because we never stored it.
      </p>
      <p className="enr-code"><code>{code}</code></p>
      <button className="g-btn g-btn-primary" onClick={() => window.location.reload()}>
        I have written it down
      </button>
    </div>
  );

  const T = {
    enrol:   { h: 'Enrol', verb: 'Enrol', label: 'Carry your record' },
    signin:  { h: 'Sign in', verb: 'Sign in', label: 'Already enrolled' },
    recover: { h: 'Recover your record', verb: 'Set a new passphrase', label: 'Forgotten passphrase' },
    reset:   { h: 'Set a new passphrase', verb: 'Set it', label: 'From your reset link' },
  }[mode];

  return (
    <form className="g-glass shell-panel" onSubmit={go}>
      <div className="g-label">{T.label}</div>
      <h3 className="g-h2 enr-h">{T.h}</h3>
      <p className="g-body">
        {mode === 'reset'
          ? 'This link already knows whose record it is — it was sent to the contact that forms the identity. Choose a new passphrase and it takes effect immediately.'
          : mode === 'recover'
          ? 'Your name, contact and the recovery code you wrote down. This sets a new passphrase and issues a new code. If you no longer have the code, ask for a link to be sent to your contact instead.'
          : mode === 'enrol'
            ? 'Your name and EMAIL identify you — sign in with those two and your passphrase on any device, here or at coc.ankr.in, and your work comes with you. The phone is so we can reach you about the course; it is not part of your sign-in.'
            : 'Your name and contact identify you; the passphrase is what keeps the record yours. The same three on any device bring your work with you — it is one identity here and at coc.ankr.in.'}
      </p>
      <div className="enr-fields">
        {/* THE RESET LINK CARRIES THE IDENTITY, so this mode does not ask for it again. Leaving the
            two fields visible-and-required here would have made a valid link unusable: the submit
            button is disabled until they are filled, and nobody arriving from an email would know
            they had to retype what the link already knows. */}
        {mode !== 'reset' && (
          <>
            <input className="enr-in" value={f.name} onChange={set('name')}
              placeholder="Your name" autoComplete="name" required />
            {/* ── ENROLLING ASKS FOR BOTH; EVERY OTHER MODE MUST NOT ────────────────────────
                Founder, 2026-08-19: "phone email name etc for gtm funnel". Only the enrol path
                collects them, and the reason is identity rather than tidiness: a principal is
                DERIVED from name + contact, so the 148 people already enrolled derived theirs from
                whatever they typed into a single "Email or phone" box. Changing what sign-in asks
                for would silently derive a different id and refuse them their own record.
                So enrolling splits the field and the EMAIL becomes the contact that forms the
                identity — said out loud below, because a learner who signs in later with the phone
                instead would be a different person to this registry. */}
            {mode === 'enrol' ? (
              <>
                <input className="enr-in" type="email" value={f.email} onChange={set('email')}
                  placeholder="Email — this identifies you" autoComplete="email" required />
                <input className="enr-in" type="tel" value={f.phone} onChange={set('phone')}
                  placeholder="Phone (with country code)" autoComplete="tel" required />
              </>
            ) : (
              <input className="enr-in" value={f.contact} onChange={set('contact')}
                placeholder="Email or phone" autoComplete="email" required />
            )}
          </>
        )}
        {mode === 'recover' && (
          <input className="enr-in" value={f.recoveryCode} onChange={set('recoveryCode')}
            placeholder="Recovery code" autoCapitalize="characters" required />
        )}
        <input className="enr-in" type="password" value={f.passphrase} onChange={set('passphrase')}
          placeholder={mode === 'signin' ? 'Passphrase' : 'Choose a passphrase (8+ characters)'}
          autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} required minLength={8} />
        <button className="g-btn g-btn-primary"
          disabled={busy || (mode !== 'reset' && (!f.name.trim()
            || (mode === 'enrol' ? (!f.email.trim() || !f.phone.trim()) : !f.contact.trim())))}>
          {busy ? 'Working…' : T.verb}
        </button>
      </div>

      {/* ── THE OTHER WAY BACK ────────────────────────────────────────────────────────────────
          FOUNDER, 2026-08-16: "we need email reset or something, password reset". A code shown
          once is a good SECOND secret and a poor only route — a learner who did not write it down
          had no way back at all, which on a paid ladder is duty-of-care wrong.
          It is a button rather than a second form: the two fields it needs are already on screen,
          and asking for them twice is how a recovery flow starts feeling like an interrogation. */}
      {mode === 'recover' && (
        <p className="g-body enr-note">
          <button type="button" className="g-btn" disabled={busy || !f.name.trim() || !f.contact.trim()}
            onClick={askForLink}>Email me a reset link instead</button>
        </p>
      )}
      {/* Rendered as the registry's own sentence, not a paraphrase: it says the same thing whether
          or not the identity exists, and a friendlier version written here would be a second claim
          about what just happened. */}
      {sent && <p className="g-body enr-note enr-sent">{sent}</p>}

      {/* `Value` and `failed` are imported at the top, and were NOT when this component was moved
          out of App.jsx — the two names lived in App's import block and did not travel with it.
          Nothing complained: the build does not resolve a free identifier, and this line only
          evaluates when `err` is set. So the form rendered perfectly until the first WRONG
          passphrase, at which point React threw ReferenceError mid-render and the whole page went
          blank — the moment a learner most needs to be told what happened is exactly the moment it
          would have vanished. A test that only signs in successfully cannot see this. */}
      {err && <p className="g-body enr-err"><Value of={failed(err)} /></p>}
      {/* ── A REFUSED SIGN-IN OFFERS THE WAY BACK ────────────────────────────────────────────
          FOUNDER, 2026-08-16: signed in with the right name and contact — they derive to exactly
          his principal — and was refused. The passphrase was 23 characters, which is the length of
          a RECOVERY CODE (20 chars in groups of five, three dashes). He had almost certainly put
          the code he wrote down into the box that wanted the passphrase, and the two are not
          interchangeable by design: the code is a SECOND independent secret, and using it means
          setting a new passphrase.

          The server cannot say so. It answers "those details do not match an identity here" for
          both a wrong passphrase and an unknown identity, on purpose — telling them apart would
          confirm to a stranger that a named person studies here. That is the right refusal and it
          leaves the surface to supply the remedy, which is this button. Same rule as the gate
          itself: a refusal that says only "no" is read as the tool being broken.

          Shown only on SIGN-IN, because on enrol a failure means something else entirely and the
          collision case below already offers its own two routes. */}
      {err && mode === 'signin' && !claimed && (
        <p className="g-body enr-note">
          <button type="button" className="g-btn"
            onClick={() => { setMode('recover'); setErr(''); }}>Forgotten passphrase — use a recovery code</button>
        </p>
      )}
      {/* A collision with a REAL person is not a failed login and must not be dressed as one — the
          only useful next step is to sign in or recover, so it is offered here. */}
      {claimed && (
        <p className="g-body enr-note">
          <button type="button" className="g-btn" onClick={() => { setMode('signin'); setErr(''); setClaimed(false); }}>Sign in instead</button>{' '}
          <button type="button" className="g-btn" onClick={() => { setMode('recover'); setErr(''); setClaimed(false); }}>Use a recovery code</button>
        </p>
      )}

      <p className="g-body g-dim enr-note">
        {mode === 'enrol'
          ? <>Nothing here is a payment. We store no passphrase and no recovery code — only a
              slow one-way hash of each, so this box being read does not open your record.{' '}
              <button type="button" className="g-btn g-btn-quiet enr-out" onClick={() => setMode('signin')}>Already enrolled?</button></>
          : <button type="button" className="g-btn g-btn-quiet enr-out" onClick={() => { setMode('enrol'); setErr(''); }}>Enrol instead</button>}
      </p>
    </form>
  );
}